import { useQueryClient } from '@tanstack/react-query'
import { Trans, useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { getSteps } from './utils/FullWalkthrough'
import Portal from 'components/Portal'
import Walkthrough, { ID_ELEMETS_WALKTHROUGH } from 'components/Walkthrough'
import useMediaQuery from 'hooks/useMediaQuery'
import { USER_KEYS } from 'hooks/user'
import { STATUS_FLAGS } from 'utils/constants/flags'
import { getCurrentUserTypeByRole } from 'utils'
import { TYPES_LANGUAGES } from 'utils/constants/languages'
import { ALIGN, POSITION } from 'components/Walkthrough/Walkthrough.helper'

const WalkthroughContainer = ({ children, portalId }) => {
  const { isMobile } = useMediaQuery()
  const queryClient = useQueryClient()
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const { EVALUATIONS, RECOMMENDATIONS } =
    queryClient.getQueryData([USER_KEYS.USER_CONFIGURATIONS]) || {}
  const {
    name,
    role,
    username,
    userFlags: { FULL_WALKTHROUGH, SINGLE_WALKTHROUGH },
    schoolYear,
  } = queryClient.getQueryData({
    queryKey: [USER_KEYS.GET_USER],
  })
  const showFullWalkThrough = FULL_WALKTHROUGH === STATUS_FLAGS.TRUE
  const { isTeacher } = getCurrentUserTypeByRole(role)
  const showBooksSingleWalkthrough =
    SINGLE_WALKTHROUGH === STATUS_FLAGS.TRUE && isTeacher

  const userFullName = `${name || username}`
  const { yearBegin, yearEnd } = schoolYear || {}

  const getBookCardImage = () => {
    let imageUrl
    try {
      imageUrl = require(`../../assets/new-cards-${language}.png`)
    } catch (error) {
      imageUrl = require(`../../assets/new-cards-${TYPES_LANGUAGES.ES_ES}.png`)
    }
    return imageUrl
  }

  const renderWalkthrough = () => {
    if (showFullWalkThrough) {
      return (
        <Portal id={`full-walkthrough-${portalId}`}>
          <Walkthrough
            steps={getSteps({
              flags: {
                hasCatalogue: RECOMMENDATIONS,
                hasEvaluations: EVALUATIONS,
              },
              isMobile,
              role,
              user: { userFullName, yearBegin, yearEnd },
            })}
          />
        </Portal>
      )
    }

    return (
      <Portal id={`books-single-walkthrough-${portalId}`}>
        <Walkthrough
          steps={[
            {
              buttons: {
                nextTitle: t('Aceptar'),
              },
              id: ID_ELEMETS_WALKTHROUGH.BUTTON_BOOKS,
              image: {
                title: t('¡Novedad!'),
                url: getBookCardImage(),
              },
              location: {
                tablet: {
                  align: ALIGN.CENTER,
                  position: POSITION.TOP,
                },
                web: {
                  align: ALIGN.END,
                  position: POSITION.IN_LINE,
                },
              },
              newFeatureText: t('¡Novedad!'),
              subtitle: (
                <Trans
                  i18nKey={
                    'Ahora los recursos y opciones de cada libro están en el menú situado a la derecha del nombre del libro Mira el ejemplo en la imagen:'
                  }
                >
                  Ahora <strong>los recursos y opciones</strong> de cada libro
                  <strong>
                    están en el menú situado a la derecha del nombre
                  </strong>
                  del libro.
                  <br />
                  Mira el ejemplo en la imagen:
                </Trans>
              ),
              title: t('Nuevo estilo para tus libros'),
            },
          ]}
        />
      </Portal>
    )
  }

  return (
    <>
      {children}
      {(showFullWalkThrough || showBooksSingleWalkthrough) &&
        renderWalkthrough()}
    </>
  )
}

WalkthroughContainer.defaultProps = {
  hasFullWalkThrough: false,
}

WalkthroughContainer.propTypes = {
  children: PropTypes.node.isRequired,
  portalId: PropTypes.string.isRequired,
}

export default WalkthroughContainer
